import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import type { ClubwiseFileKind, ClubwiseJoinRow, ClubwiseLeadRow } from '../../lib/clubwise/csv';
import { decodeClubwiseBytes, readSlotFile, SLOT_LABEL } from '../../lib/clubwise/fileSlot';
import { defaultDataTo } from '../../lib/clubwise/upload';
import { monthName, shortDay } from '../../lib/clubwise/window';
import {
  commitClubwiseUpload,
  getClubwiseStatus,
  previewClubwiseUpload,
  type ClubwiseCommitResult,
  type ClubwisePreview,
} from './api';

// ANYTIME FITNESS — monthly ClubWise upload, on the account's Connections tab.
//
// Flow: choose the month, drop the two CSV exports, PREVIEW, then CONFIRM.
//
// ⚠️⚠️ THE FILES ARE READ IN THIS BROWSER AND ONLY SIX COLUMNS LEAVE IT (member
// number, status, source, contact type, and for joiners membership type + joining
// date). Names, phones, emails, addresses, dates of birth, postcodes and card
// numbers are never posted, stored or logged: the projection (lib/clubwise/csv)
// has no code path that reads them, and the server refuses any other field.
//
// ⚠️ Shown only for an account whose brand reads ClubWise uploads (the server
// decides, by the same registry the report uses).

function previousMonthKey(now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Today in the club's own zone (the server tells us which; AU's until it has), YYYY-MM-DD. */
function clubToday(timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

function formatWhen(iso: string): string {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? iso : new Date(t).toLocaleString('en-AU');
}

interface SlotState {
  fileName: string;
  rows: ClubwiseLeadRow[] | ClubwiseJoinRow[];
}

export function ClubwiseUploadSection({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const [month, setMonth] = useState<string>(previousMonthKey());
  // ⚠️ null = not touched: the default follows the month (today for this month, else its last day).
  const [dataToEdit, setDataToEdit] = useState<string | null>(null);
  const [leads, setLeads] = useState<SlotState | null>(null);
  const [joins, setJoins] = useState<SlotState | null>(null);
  const [slotError, setSlotError] = useState<Partial<Record<ClubwiseFileKind, string>>>({});
  const [preview, setPreview] = useState<ClubwisePreview | null>(null);
  const [result, setResult] = useState<ClubwiseCommitResult | null>(null);
  const leadsInput = useRef<HTMLInputElement>(null);
  const joinsInput = useRef<HTMLInputElement>(null);

  const status = useQuery({
    queryKey: ['clubwise', 'status', accountId],
    queryFn: () => getClubwiseStatus(accountId),
  });

  // ⚠️ The club's day, not the browser's and not always Melbourne's: a UK club's "today" is London's.
  const today = clubToday(status.data?.timeZone ?? 'Australia/Melbourne');
  const dataTo = dataToEdit ?? defaultDataTo(month, today);

  const body = () => ({
    month,
    dataTo,
    leads: (leads?.rows ?? []) as ClubwiseLeadRow[],
    joins: (joins?.rows ?? []) as ClubwiseJoinRow[],
  });

  const clearFiles = () => {
    setLeads(null);
    setJoins(null);
    setPreview(null);
    if (leadsInput.current) leadsInput.current.value = '';
    if (joinsInput.current) joinsInput.current.value = '';
  };

  const previewMutation = useMutation({
    mutationFn: () => previewClubwiseUpload(accountId, body()),
    onSuccess: (p) => {
      setResult(null);
      setPreview(p);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const commitMutation = useMutation({
    mutationFn: () => commitClubwiseUpload(accountId, body()),
    onSuccess: (r) => {
      setResult(r);
      clearFiles();
      void queryClient.invalidateQueries({ queryKey: ['clubwise', 'status', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['account-history', accountId] });
      toast.success(`${monthName(r.month)} uploaded.`);
      if (!r.auditRecorded) {
        toast.warning(
          'The upload was saved, but its history entry could not be written. It is still recorded in the upload list.',
          { duration: 12_000 },
        );
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleFile = async (slot: ClubwiseFileKind, file: File) => {
    setPreview(null);
    setResult(null);
    try {
      const text = decodeClubwiseBytes(await file.arrayBuffer());
      const r = readSlotFile(slot, text);
      if (!r.ok) {
        setSlotError((e) => ({ ...e, [slot]: r.error }));
        (slot === 'leads' ? setLeads : setJoins)(null);
        return;
      }
      setSlotError((e) => ({ ...e, [slot]: undefined }));
      (slot === 'leads' ? setLeads : setJoins)({ fileName: file.name, rows: r.rows });
    } catch {
      setSlotError((e) => ({ ...e, [slot]: 'That file could not be read.' }));
    }
  };

  if (status.isLoading) return <Spinner />;
  const s = status.data;
  // ⚠️ Not a ClubWise brand → the section does not exist for this account.
  if (!s || !s.isClubwiseBrand) return null;

  if (!s.clubwiseCode) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">
          Set this club&apos;s <strong>ClubWise code</strong> (its member-number prefix) in the account
          form before uploading ClubWise exports.
        </p>
      </Shell>
    );
  }

  const ready = leads !== null && joins !== null && /^\d{4}-\d{2}$/.test(month);
  const a = preview?.analysis;

  return (
    <Shell>
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Club code <Badge variant="secondary">{s.clubwiseCode}</Badge>. Upload the two ClubWise CSV
        exports for one month. Only six columns are read from them, in this browser; names, contact
        details and dates of birth are never sent or stored.
      </p>

      {/* uploaded months */}
      <div className="rounded-md border p-3">
        <p className="mb-2 text-sm font-medium">Uploaded months</p>
        {s.uploadsError && <p className="text-sm text-amber-700">{s.uploadsError}</p>}
        {!s.uploadsError && s.uploads.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing uploaded yet.</p>
        )}
        {s.uploads.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-1 pr-3 font-medium">Month</th>
                <th className="py-1 pr-3 font-medium">Data to</th>
                <th className="py-1 pr-3 font-medium">Leads</th>
                <th className="py-1 pr-3 font-medium">Joins</th>
                <th className="py-1 pr-3 font-medium">Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {s.uploads.map((u) => (
                <tr key={u.uploadId} className="border-t">
                  <td className="py-1 pr-3">{monthName(u.month)}</td>
                  <td className="py-1 pr-3">{u.dataTo ? shortDay(u.dataTo) : 'Whole month'}</td>
                  <td className="py-1 pr-3">{u.leadRows}</td>
                  <td className="py-1 pr-3">{u.joinRows}</td>
                  <td className="py-1 pr-3 text-muted-foreground">
                    {formatWhen(u.uploadedAt)} by {u.uploadedByEmail}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* choose month + files */}
      <div className="grid gap-3 sm:grid-cols-4">
        <label className="text-sm">
          <span className="mb-1 block font-medium">Month the exports were run for</span>
          <input
            type="month"
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              setDataToEdit(null);
              setPreview(null);
            }}
            className="w-full rounded-md border px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium">Data up to</span>
          <input
            type="date"
            value={dataTo}
            min={/^\d{4}-\d{2}$/.test(month) ? `${month}-01` : undefined}
            max={today}
            onChange={(e) => {
              setDataToEdit(e.target.value || null);
              setPreview(null);
            }}
            className="w-full rounded-md border px-2 py-1.5 text-sm"
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            The last day the exports cover. Leads have no date, so this is how far this month&apos;s leads go.
          </span>
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium">{SLOT_LABEL.leads} (CSV)</span>
          <input
            ref={leadsInput}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile('leads', f);
            }}
            className="w-full text-sm"
          />
          {leads && (
            <span className="mt-1 block text-xs text-muted-foreground">
              {leads.fileName}: {leads.rows.length} rows read
            </span>
          )}
          {slotError.leads && <span className="mt-1 block text-xs text-red-700">{slotError.leads}</span>}
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium">{SLOT_LABEL.joins} (CSV)</span>
          <input
            ref={joinsInput}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile('joins', f);
            }}
            className="w-full text-sm"
          />
          {joins && (
            <span className="mt-1 block text-xs text-muted-foreground">
              {joins.fileName}: {joins.rows.length} rows read
            </span>
          )}
          {slotError.joins && <span className="mt-1 block text-xs text-red-700">{slotError.joins}</span>}
        </label>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={!ready || previewMutation.isPending}
          onClick={() => previewMutation.mutate()}
        >
          {previewMutation.isPending ? <Spinner /> : <Upload className="mr-1 h-4 w-4" />}
          Preview
        </Button>
        {(leads || joins) && (
          <Button type="button" variant="ghost" onClick={clearFiles}>
            Clear files
          </Button>
        )}
      </div>

      {/* preview */}
      {preview && a && (
        <div className="space-y-3 rounded-md border p-3">
          <p className="text-sm font-medium">
            Preview: {preview.accountName}, {monthName(preview.month)}
          </p>

          {a.errors.length > 0 && (
            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
              <p className="mb-1 font-medium">This cannot be uploaded:</p>
              <ul className="list-disc pl-5">
                {a.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          {preview.existingUpload && (
            <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                {monthName(preview.month)} was already uploaded ({formatWhen(preview.existingUpload.uploadedAt)}{' '}
                by {preview.existingUpload.uploadedByEmail}: {preview.existingUpload.leadRows} leads,{' '}
                {preview.existingUpload.joinRows} joins). <strong>Confirming replaces it completely.</strong>
              </p>
            </div>
          )}
          {preview.existingUploadError && (
            <p className="text-sm text-amber-700">{preview.existingUploadError}</p>
          )}

          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Figure label="Leads (New Prospects)" value={a.counts.leads} />
            <Figure label="Month joins (Joiners)" value={a.counts.monthJoins} hint="Counted as sales" />
            <Figure label="Cohort joins" value={a.counts.cohortJoins} hint="This month's leads who joined" />
            <Figure label="Digital leads" value={a.counts.digitalLeads} />
          </dl>
          <p className="text-xs text-muted-foreground">
            Month joins and cohort joins differ because some joiners were prospects in an earlier month. {a.counts.leadsWithJoinedStatus} lead
            {a.counts.leadsWithJoinedStatus === 1 ? '' : 's'} carry a joined Status.
          </p>

          {a.warnings.length > 0 && (
            <ul className="list-disc pl-5 text-sm text-amber-800">
              {a.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}

          {a.unknownStatuses.length > 0 && (
            <ValueTable
              title="Statuses we do not recognise (stored as they are, not counted as joined)"
              rows={a.unknownStatuses.map((u) => ({ value: u.value, count: u.count, note: '' }))}
            />
          )}

          {a.valuesRemoved > 0 && (
            <p className="text-sm text-amber-800">
              {a.valuesRemoved} Source / Contact Type value{a.valuesRemoved === 1 ? '' : 's'} looked like a phone number or
              email and {a.valuesRemoved === 1 ? 'was' : 'were'} removed (values removed). They are not stored.
            </p>
          )}
          {a.otherValues.length > 0 && (
            <ValueTable
              title="Landed in Other (check for a new spelling of Meta, Website or Staff generated)"
              rows={a.otherValues.map((x) => ({
                value: `${x.source === '' ? 'Not recorded' : x.source} / ${x.contactType === '' ? 'Not recorded' : x.contactType}`,
                count: x.count,
                note: 'Source / Contact Type',
              }))}
            />
          )}

          <ValueTable
            title="Lead sources and whether each is counted digital"
            rows={a.sources.map((x) => ({
              value: x.value === '' ? 'Not recorded' : x.value,
              count: x.count,
              note: x.isDigital ? `Digital (${digitalClassLabel(x.digitalClass)})` : 'Not digital',
            }))}
          />
          <ValueTable
            title="Contact types and whether each is counted digital"
            rows={a.contactTypes.map((x) => ({
              value: x.value === '' ? 'Not recorded' : x.value,
              count: x.count,
              note: x.isDigital ? 'Digital' : 'Not digital',
            }))}
          />

          <div className="flex gap-2">
            <Button
              type="button"
              disabled={a.errors.length > 0 || commitMutation.isPending}
              onClick={() => commitMutation.mutate()}
            >
              {commitMutation.isPending ? <Spinner /> : null}
              {preview.existingUpload ? 'Confirm and replace' : 'Confirm and upload'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setPreview(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* result */}
      {result && (
        <div className="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-900">
          <p className="font-medium">
            {monthName(result.month)} {result.replacedExisting ? 'replaced' : 'uploaded'}.
          </p>
          <p>
            {result.counts.leads} leads, {result.counts.monthJoins} joins, {result.counts.cohortJoins}{' '}
            cohort joins, {result.counts.digitalLeads} digital leads. It will appear on the club&apos;s
            report from the next load.
          </p>
        </div>
      )}
    </div>
    </Shell>
  );
}

// ⚠️ The section shell matches AccountDetail's own Section (it is private to that
// file). It exists here so the HEADING appears only for an account that reads
// ClubWise uploads: a Snap club's Connections tab has no "ClubWise uploads".
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-md border border-slate-200 p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">ClubWise uploads</h2>
      </div>
      {children}
    </section>
  );
}

function digitalClassLabel(c: string | null): string {
  switch (c) {
    case 'meta':
      return 'Meta';
    case 'website':
      return 'Website';
    default:
      return 'digital';
  }
}

function Figure({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="rounded-md border p-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold">{value}</dd>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ValueTable({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ value: string; count: number; note: string }>;
}) {
  if (rows.length === 0) return null;
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-muted-foreground">{title}</p>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.value}|${r.note}`} className="border-t">
              <td className="py-1 pr-3">{r.value}</td>
              <td className="py-1 pr-3">{r.count}</td>
              <td className="py-1 text-muted-foreground">{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
